/**
 * Mentor development plan + live setup checklist (founder decision 2026-10-16).
 *
 * Mentors get a personal plan about their mentoring craft, as coaches get theirs, and the live setup
 * checklist stays its own card. Both cards share one checklist load, so the plan's "Start here" is
 * always the checklist's NEXT step, with the same "do it now" link.
 */
import React, { useEffect, useRef } from 'react';
import { useRouter } from 'expo-router';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useResponsive } from '@/hooks/useResponsive';
import { useTranslation } from '@/hooks/useTranslation';
import { trackAuthEvent } from '@/services/analytics';
import type { DevelopmentPlanArtifact } from '@/types/icfProgress';
import {
  MentorSetupChecklistCard,
  useMentorSetupChecklist,
  type MentorChecklistController,
} from './MentorSetupChecklistCard';
import { resolveStartHere } from './setupChecklist';

type Surface = 'dashboard' | 'progress';

interface Props {
  plan: DevelopmentPlanArtifact;
  surface: Surface;
  locked?: boolean;
  /** The checklist card keeps this id (and `${testID}_progress`); the plan card is `${testID}_mentor_plan`. */
  testID: string;
}

export function MentorPlanWithChecklist({ plan, surface, locked = false, testID }: Props) {
  const controller = useMentorSetupChecklist({ mentorPlan: true });
  return (
    <View style={styles.stack}>
      <MentorPlanCard plan={plan} surface={surface} locked={locked} controller={controller} testID={`${testID}_mentor_plan`} />
      <MentorSetupChecklistCard surface={surface} mentorPlan controller={controller} testID={testID} />
    </View>
  );
}

function MentorPlanCard({ plan, surface, locked, controller, testID }: {
  plan: DevelopmentPlanArtifact;
  surface: Surface;
  locked: boolean;
  controller: MentorChecklistController;
  testID: string;
}) {
  const router = useRouter();
  const { t, language } = useTranslation();
  const { isPhone } = useResponsive();
  const tracked = useRef(false);
  const startHere = resolveStartHere(plan.start_here_step, controller.state);

  useEffect(() => {
    if (tracked.current) return;
    tracked.current = true;
    trackAuthEvent('progress_plan_viewed', { source: plan.source, version: plan.version, plan_kind: plan.plan_kind, surface });
  }, [plan.source, plan.version, plan.plan_kind, surface]);

  const practiceByTitle = new Map((plan.practice_items || []).map((item) => [item.title, item.body]));
  const focusAreas = locked ? plan.focus_areas.slice(0, 1) : plan.focus_areas;

  return (
    <View testID={testID} style={styles.card}>
      <View style={[styles.header, isPhone && styles.headerPhone]}>
        <View style={styles.titleRow}>
          <View style={styles.icon}>
            <MaterialIcons name="auto-awesome" size={21} color={reviewDesk.accent} />
          </View>
          <View style={styles.headerCopy}>
            <Text style={styles.eyebrow}>{t('mentorPlan.eyebrow')}</Text>
            <Text selectable testID={`${testID}_headline`} style={styles.title}>{plan.headline}</Text>
            {!!plan.summary && <Text selectable style={styles.subtitle}>{plan.summary}</Text>}
          </View>
        </View>
        <Text selectable style={styles.version}>{t('icfProgress.plan.version', { version: plan.version })}</Text>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{t('intake.plan.focusHeading')}</Text>
        {focusAreas.map((area, index) => {
          const practice = practiceByTitle.get(area.title);
          return (
            <View key={`${area.competency || index}-${area.title}`} style={styles.focusItem}>
              <View style={styles.number}><Text style={styles.numberText}>{index + 1}</Text></View>
              <View style={styles.itemCopy}>
                <Text selectable testID="plan_focus_title" style={styles.itemTitle}>{area.title}</Text>
                {!!area.why && <Text selectable style={styles.itemBody}>{area.why}</Text>}
                {!!practice && <Text selectable style={styles.itemBody}>{t('intake.plan.practiceText', { practice })}</Text>}
              </View>
            </View>
          );
        })}
      </View>

      {!locked && (
        <View style={[styles.details, isPhone && styles.detailsPhone]}>
          {!!plan.next_milestone && <Detail label={t('intake.plan.nextMilestoneLabel')} value={plan.next_milestone} />}
          {!!plan.weekly_practice && <Detail label={t('intake.plan.weeklyPracticeLabel')} value={plan.weekly_practice} />}
        </View>
      )}

      <View testID={`${testID}_start_here`} style={styles.startHere}>
        <Text style={styles.startLabel}>{t('intake.plan.firstActionLabel')}</Text>
        {startHere.kind === 'complete' ? (
          <>
            <Text selectable style={styles.startTitle}>{t('mentorPlan.setupDoneTitle')}</Text>
            <Text selectable style={styles.itemBody}>{t('mentorPlan.setupDoneBody')}</Text>
          </>
        ) : (
          <>
            <Text selectable testID={`${testID}_start_here_step`} style={styles.startTitle}>
              {t(`mentorChecklist.steps.${startHere.step}.title`)}
            </Text>
            <Text selectable style={styles.itemBody}>
              {startHere.personal && plan.first_action ? plan.first_action : t(`mentorChecklist.steps.${startHere.step}.why`)}
            </Text>
            {startHere.href && !locked ? (
              <Pressable
                testID={`${testID}_start_here_cta`}
                accessibilityRole="button"
                onPress={() => {
                  trackAuthEvent('mentor_plan_start_here_clicked', { surface, step: startHere.step, personal: startHere.personal });
                  router.push(startHere.href as never);
                }}
                style={({ pressed }) => [styles.cta, pressed && { opacity: 0.85 }]}
              >
                <Text style={styles.ctaText}>{t('mentorChecklist.doItNow')}</Text>
                <MaterialIcons name="arrow-forward" size={16} color="#FFFFFF" />
              </Pressable>
            ) : null}
          </>
        )}
      </View>

      <View style={styles.footer}>
        <Text selectable style={styles.updated}>
          {t('icfProgress.plan.updated', { date: new Date(plan.updated_at).toLocaleDateString(language) })}
        </Text>
        {locked && <Text selectable style={styles.locked}>{t('icfProgress.plan.lockedTeaser')}</Text>}
      </View>
    </View>
  );
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <View style={styles.detail}>
      <Text style={styles.detailLabel}>{label}</Text>
      <Text selectable style={styles.itemBody}>{value}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  stack: { width: '100%', gap: 18 },
  card: { width: '100%', backgroundColor: reviewDesk.card, borderWidth: 1, borderColor: reviewDesk.line, borderRadius: 18, padding: 18, gap: 16 },
  header: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 },
  headerPhone: { flexDirection: 'column' },
  titleRow: { flex: 1, flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  icon: { width: 38, height: 38, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  headerCopy: { flex: 1, minWidth: 0, gap: 4 },
  eyebrow: { color: reviewDesk.accent, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  title: { color: reviewDesk.ink, fontSize: 18, fontWeight: '800', lineHeight: 24 },
  subtitle: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  version: { color: reviewDesk.accent, fontSize: 12, fontWeight: '700', backgroundColor: reviewDesk.accentSoft, borderRadius: 999, paddingHorizontal: 9, paddingVertical: 5 },
  section: { gap: 12 },
  sectionTitle: { color: reviewDesk.ink, fontSize: 14, fontWeight: '800' },
  focusItem: { flexDirection: 'row', alignItems: 'flex-start', gap: 11 },
  number: { width: 27, height: 27, borderRadius: 14, alignItems: 'center', justifyContent: 'center', backgroundColor: reviewDesk.accentSoft },
  numberText: { color: reviewDesk.accent, fontWeight: '800', fontVariant: ['tabular-nums'] },
  itemCopy: { flex: 1, minWidth: 0, gap: 4 },
  itemTitle: { color: reviewDesk.ink, fontSize: 15, fontWeight: '700' },
  itemBody: { color: reviewDesk.inkSoft, fontSize: 13, lineHeight: 19 },
  details: { flexDirection: 'row', gap: 14, borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 12 },
  detailsPhone: { flexDirection: 'column' },
  detail: { flex: 1, minWidth: 0, gap: 4 },
  detailLabel: { color: reviewDesk.muted, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  startHere: { gap: 6, padding: 14, borderRadius: 12, backgroundColor: reviewDesk.successSoft, borderWidth: 1, borderColor: reviewDesk.success },
  startLabel: { color: reviewDesk.success, fontSize: 11, fontWeight: '800', textTransform: 'uppercase', letterSpacing: 0.5 },
  startTitle: { color: reviewDesk.ink, fontSize: 15, fontWeight: '800' },
  cta: { alignSelf: 'flex-start', flexDirection: 'row', alignItems: 'center', gap: 6, minHeight: 40, marginTop: 4, paddingHorizontal: 14, borderRadius: 12, backgroundColor: reviewDesk.accent },
  ctaText: { color: '#FFFFFF', fontSize: 13, fontWeight: '700' },
  footer: { borderTopWidth: StyleSheet.hairlineWidth, borderTopColor: reviewDesk.line, paddingTop: 10, gap: 5 },
  updated: { color: reviewDesk.muted, fontSize: 11 },
  locked: { color: reviewDesk.accent, fontSize: 12, lineHeight: 17, fontWeight: '700' },
});

export default MentorPlanWithChecklist;
